import { useEffect, useState } from "react";
import { useRealtimeKitSelector } from "@cloudflare/realtimekit-react";
import type RTKClient from "@cloudflare/realtimekit";
import type { RTKParticipant } from "@cloudflare/realtimekit";
import { getStageFeedParticipants } from "./livestreamStageFeeds";

export function useLivestreamStageFeeds({
  meeting,
  includeSelf,
}: {
  meeting: RTKClient | undefined;
  includeSelf: boolean;
}) {
  const joinedParticipants = useRealtimeKitSelector((m) =>
    m.participants.joined.toArray(),
  );
  const selfVideoEnabled = useRealtimeKitSelector((m) => m.self.videoEnabled);
  const selfAudioEnabled = useRealtimeKitSelector((m) => m.self.audioEnabled);
  const selfScreenShareEnabled = useRealtimeKitSelector((m) => m.self.screenShareEnabled);
  const stageStatus = useRealtimeKitSelector((m) => m.stage.status);
  const [feeds, setFeeds] = useState<RTKParticipant[]>([]);

  useEffect(() => {
    if (!meeting) {
      setFeeds([]);
      return;
    }

    const sync = () => {
      setFeeds(getStageFeedParticipants({ meeting, includeSelf }));
    };

    const attachParticipantMediaListeners = (participant: RTKParticipant) => {
      participant.on("videoUpdate", sync);
      participant.on("audioUpdate", sync);
      participant.on("screenShareUpdate", sync);
    };

    const detachParticipantMediaListeners = (participant: RTKParticipant) => {
      participant.off("videoUpdate", sync);
      participant.off("audioUpdate", sync);
      participant.off("screenShareUpdate", sync);
    };

    sync();
    meeting.participants.joined.toArray().forEach(attachParticipantMediaListeners);
    meeting.participants.active.on("participantsUpdate", sync);
    const onParticipantJoined = (participant: RTKParticipant) => {
      attachParticipantMediaListeners(participant);
      sync();
    };
    const onParticipantLeft = (participant: RTKParticipant) => {
      detachParticipantMediaListeners(participant);
      sync();
    };
    meeting.participants.joined.on("participantJoined", onParticipantJoined);
    meeting.participants.joined.on("participantLeft", onParticipantLeft);
    meeting.stage.on("stageStatusUpdate", sync);

    const trackSelfMedia = includeSelf || stageStatus === "ON_STAGE";

    if (trackSelfMedia) {
      meeting.self.on("videoUpdate", sync);
      meeting.self.on("audioUpdate", sync);
      meeting.self.on("screenShareUpdate", sync);
    }

    return () => {
      meeting.participants.joined.toArray().forEach(detachParticipantMediaListeners);
      meeting.participants.active.off("participantsUpdate", sync);
      meeting.participants.joined.off("participantJoined", onParticipantJoined);
      meeting.participants.joined.off("participantLeft", onParticipantLeft);
      meeting.stage.off("stageStatusUpdate", sync);

      if (trackSelfMedia) {
        meeting.self.off("videoUpdate", sync);
        meeting.self.off("audioUpdate", sync);
        meeting.self.off("screenShareUpdate", sync);
      }
    };
  }, [
    meeting,
    includeSelf,
    joinedParticipants,
    selfVideoEnabled,
    selfAudioEnabled,
    selfScreenShareEnabled,
    stageStatus,
  ]);

  return feeds;
}
